<template>
  <div class="form__element">
    <div class="form__textarea"
         ref="textarea"
         :class="{'form__textarea--error': hasError, 'form__textarea--negative' : inverted}">
      <textarea class="form__textarea-field"
                :aria-invalid="hasError ? 'true' : null"
                :aria-describedby="hasError ? `error-${id}-${inputId}` : null"
                :value="modelValue"
                @input="handleInput"
                autocomplete="off"
                @blur="handleBlur"
                rows="3"
                :id="id + '-' + inputId" />
      <span class="form__label">
        <label class="label"
               :for="id + '-' + inputId">{{ label }}</label>
      </span>
    </div>
    <Error v-if="hasError"
           :inverted="inverted"
           :id="'error-' + id + '-' + inputId"
           :message="errorMessage" />
  </div>
</template>

<script setup>
import {ref, computed, watch, useId, useTemplateRef, onMounted} from 'vue';
import Error from './Error.vue';
import Textarea from "/vendor/skygate/kieser-patternlab/source/javascript/components/textarea";

const textarea = useTemplateRef('textarea');

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  modelValue: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  rules: {
    type: Array,
    default: () => [],
  },
  forceValidation: {
    type: Boolean,
    default: false,
  },
  inverted: {
    type: Boolean,
    default: false,
  }
});

const emit = defineEmits(['update:modelValue', 'validation-change']);

const internalValue = ref(props.modelValue);
const isTouched = ref(false);
const errorMessage = ref('');
const inputId = useId();

onMounted(() => {
  new Textarea(textarea.value);
});

watch(() => props.modelValue, (newValue) => {
  internalValue.value = newValue;
});

const validate = () => {
  for (const rule of props.rules) {
    const result = rule(internalValue.value);
    if (typeof result === 'string') {
      errorMessage.value = result;
      emit('validation-change', false);
      return false;
    }
  }
  errorMessage.value = '';
  emit('validation-change', true);
  return true;
};

watch([internalValue, () => props.forceValidation, isTouched], () => {
  if (isTouched.value || props.forceValidation) {
    validate();
  } else {
    errorMessage.value = '';
  }
}, { immediate: true });


const handleInput = (event) => {
  internalValue.value = event.target.value;
  emit('update:modelValue', internalValue.value);
};

const handleBlur = () => {
  isTouched.value = true;
  validate();
};

const hasError = computed(() => (isTouched.value || props.forceValidation) && errorMessage.value !== '');
</script>